import axios from 'axios';

const AUTH_STORAGE_KEYS = new Set([
  'token',
  'api_token',
  'access_token',
  'auth_token',
  'refresh_token',
  'id_token',
  'bearer_token',
  'jwt',
  'student',
  'user',
  'currentUser',
  'current_user',
  'authorization',
  'auth',
  'authState',
  'auth_state',
  'authStore',
  'auth_store',
  'authSession',
  'auth_session',
  'vuex-auth',
  'vuex_auth',
  'pinia-auth',
  'pinia_auth',
].map(normalizeStorageKey));

const PERSISTED_STORE_CONTAINER_KEYS = new Set([
  'vuex',
  'pinia',
  'persistedState',
  'persisted_state',
  'vuexPersistedState',
  'piniaPersistedState',
].map(normalizeStorageKey));

function normalizeStorageKey(key) {
  return String(key || '').toLowerCase().replace(/[\s._:-]/g, '');
}

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isAuthenticationKey(key) {
  const normalized = normalizeStorageKey(key);
  return AUTH_STORAGE_KEYS.has(normalized)
    || /^(auth|authentication)(data|state|store|session|token|user)?$/.test(normalized)
    || /^(vuex|pinia|persist|persistedstate)(auth|authentication|session|token|user)$/.test(normalized);
}

function removeAuthProperties(value) {
  if (Array.isArray(value)) {
    value.forEach(removeAuthProperties);
    return value;
  }

  if (!isPlainObject(value)) return value;

  Object.keys(value).forEach((key) => {
    if (isAuthenticationKey(key)) {
      delete value[key];
      return;
    }

    removeAuthProperties(value[key]);
  });

  return value;
}

function clearPersistedStoreAuth(storage, key) {
  const rawValue = storage.getItem(key);
  if (!rawValue) return;

  try {
    const state = JSON.parse(rawValue);
    if (!isPlainObject(state) && !Array.isArray(state)) return;

    const sanitizedState = removeAuthProperties(state);
    const isEmptyObject = isPlainObject(sanitizedState) && Object.keys(sanitizedState).length === 0;

    if (isEmptyObject) {
      storage.removeItem(key);
    } else {
      storage.setItem(key, JSON.stringify(sanitizedState));
    }
  } catch {
    // A generic persistence container may contain non-JSON data. Leave it alone
    // instead of risking unrelated preferences.
  }
}

function clearStorageAuthentication(storage) {
  if (!storage) return;

  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (!key) continue;

    if (isAuthenticationKey(key)) {
      storage.removeItem(key);
      continue;
    }

    if (PERSISTED_STORE_CONTAINER_KEYS.has(normalizeStorageKey(key))) {
      clearPersistedStoreAuth(storage, key);
    }
  }
}

function browserStorage(name) {
  try {
    return typeof window !== 'undefined' ? window[name] : null;
  } catch {
    return null;
  }
}

export function clearPersistedAuthentication() {
  clearStorageAuthentication(browserStorage('localStorage'));
  clearStorageAuthentication(browserStorage('sessionStorage'));
}

export function removeAuthorizationHeader(headers) {
  if (!headers) return;

  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
    headers.delete('authorization');
  }

  Object.keys(headers).forEach((key) => {
    if (key.toLowerCase() === 'authorization') delete headers[key];
  });
}

export function clearAxiosAuthorization(axiosInstance = axios) {
  removeAuthorizationHeader(axiosInstance?.defaults?.headers?.common);
}

export function resetAuthenticationForGuest(store, axiosInstance = axios) {
  clearPersistedAuthentication();
  store?.commit?.('logout');
  clearAxiosAuthorization(axiosInstance);
}

export function isReservationPath(path) {
  const normalizedPath = String(path || '').replace(/\/+$/, '') || '/';
  return normalizedPath === '/reservation';
}
